import { backendApi } from "@/services/api";
import type {
  Condition,
  State,
} from "@/services/backend/deliveries/state/state";
import {
  DeliveryAnnouncement,
  SampleSetAnnouncement,
  type Delivery,
} from "@/services/backend/deliveries/delivery/delivery";
import { EntityId } from "@/shared/nidavellir/types/entity-id";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { ActiveAirMicrobialMetadata, SamplingMetadata } from "../../types";

const deliveriesApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    deliveriesList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/inspections/deliveries`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["Delivery"],
    }),
    putDelivery: build.mutation<Delivery, PutDeliveryRequest>({
      query: (request) => ({
        url: `/inspections/deliveries`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["Delivery"],
    }),
    updateDeliveryCondition: build.mutation<Delivery, UpdateConditionRequest>({
      query: (request) => ({
        url: `/inspections/deliveries/update-condition`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: ["Delivery"],
    }),
    updateDeliveryState: build.mutation<Delivery, UpdateStateRequest>({
      query: (request) => ({
        url: `/inspections/deliveries/update-state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: ["Delivery"],
    }),
    updateSamplingMetadata: build.mutation<Delivery, UpdateSamplingMetadata>({
      query: (request) => ({
        url: `/inspections/deliveries/sampling-metadata`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: ["Delivery"],
    }),
    getSamplingMetadata: build.query<
      GetSamplingMetadataResponse,
      GetSamplingMetadataRequest
    >({
      query: (request) => ({
        params: request,
        url: `/inspections/deliveries/sampling-metadata`,
        method: "GET",
      }),
      providesTags: ["Delivery"],
    }),
    getDelivery: build.query<Delivery, GetRequest>({
      query: (request) => ({
        url: `/inspections/deliveries/${request.id}`,
        method: "GET",
      }),
      providesTags: (_result, _e, request) => [
        { type: "Delivery", id: request.id },
      ],
    }),
    getDeliveryByProcess: build.query<Delivery, GetByProcessRequest>({
      query: (request) => ({
        url: `/inspections/deliveries-by-process/${request.processId}`,
        method: "GET",
      }),
      providesTags: ["Delivery"],
    }),
    announceDelivery: build.mutation<
      DeliveryAnnouncement,
      AnnounceDeliveryRequest
    >({
      query: (request) => ({
        url: `/inspections/announcements-internal`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["Delivery", "DeliveryTemplate"],
    }),
  }),
});

export const {
  useDeliveriesListQuery,
  useGetDeliveryQuery,
  usePutDeliveryMutation,
  useAnnounceDeliveryMutation,
  useGetDeliveryByProcessQuery,
  useUpdateDeliveryConditionMutation,
  useUpdateDeliveryStateMutation,
  useGetSamplingMetadataQuery,
  useUpdateSamplingMetadataMutation,
} = deliveriesApi;

export interface UpdateConditionRequest {
  id: string;
  condition: Condition;
}

export interface UpdateStateRequest {
  id: string;
  state: State;
  employeeId: string;
}

export interface UpdateSamplingMetadata {
  id: string;
  samplingMetadata: SamplingMetadata;
  activeAirMicrobialMetadata: ActiveAirMicrobialMetadata;
}

export interface GetRequest {
  id: string;
}

export interface GetByProcessRequest {
  processId: string;
}

export interface DeleteRequest {
  id: string;
}

export interface ListRequest {
  ownerId?: Filter<string>;
  ownerType?: Filter<string>;
  condition?: Filter<Condition>;
  state?: Filter<State>;
  orderBy?: OrdrBy<string>[];
  limit: number;
  offset: number;
}

export interface List {
  count: number;
  items: Delivery[];
}

export interface PutDeliveryRequest {
  id: string;
  customerId?: string;
  description?: string;
  shippingDate?: string;
  deliveryDate?: string;
  creationDate?: string;
  deliveryState?: State;
  packageCondition?: Condition;
  isComplete?: boolean;
  trackingId?: string;
}

export interface AnnounceDeliveryRequest {
  id: string;
  customerId: EntityId;
  trackingId: string | undefined;
  description: string;
  sampleSets: SampleSetAnnouncement[];
}

export interface GetSamplingMetadataRequest {
  deliveryId: string;
}

export interface GetSamplingMetadataResponse {
  samplingMetadata: SamplingMetadata;
  activeAirMicrobialMetadata: ActiveAirMicrobialMetadata;
}
